import { RadioGroup } from '@headlessui/react';
import { ArchiveBoxXMarkIcon, CalendarDaysIcon, CheckCircleIcon, PhoneXMarkIcon } from '@heroicons/react/24/outline';
import { PhoneIcon, UserGroupIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import { Button, Datepicker, Label, Textarea } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatDateTimeForDb, updateTimeInDate } from '../../../../../common-utils/formatters';
import type { InteractionFormData } from '../../../../@types/contactInteraction';
import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import { OWNER_STEPS } from '../../../../@types/Owner';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization } from '../../../../utils/JWTHeader';
import Badge from '../../../common/Badge';
import Popup from '../../../common/Popup/Popup.module';
import { handleContactClick } from '../../OwnerCommunication/utils/OwnerCommunicationService';

interface InteractionFormProps {
  isOpen: boolean;
  onClose: () => void;
  owner: Owner;
  onSubmit: (data: InteractionFormData) => void;
  jwt: string;
  onRefresh?: () => void;
}

// Local FormData that includes interactionAt which is required by the backend
interface FormData {
  interactionType: ContactInteractionType;
  interactionAt: Date;
  meetingAt: Date;
  meetingWith: number | null;
  meetingWithType: string | null;
  comment: string | null;
}

type FormStep = 'initial' | 'response' | 'full';

const InteractionForm: React.FC<InteractionFormProps> = ({ 
  isOpen, 
  onClose, 
  owner, 
  onSubmit, 
  jwt,
  onRefresh,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [formStep, setFormStep] = useState<FormStep>('initial');
  const [formData, setFormData] = useState<FormData>({
    interactionType: ContactInteractionType.PHONE,
    interactionAt: new Date(),
    meetingAt: new Date(),
    meetingWith: null,
    meetingWithType: 'sales-manager',
    comment: null,
  });
  const [showClasserInjoignable, setShowClasserInjoignable] = useState<boolean>(false);
  const [sessionAttempts, setSessionAttempts] = useState<number>(0);

  const CONSECUTIVE_ATTEMPTS_THRESHOLD = 3;

  const getConsecutivePhoneContacts = (): number => {
    const allInteractions: { isContactAttempt: boolean; date: string }[] = [];

    if (owner.contacts && owner.contacts.length > 0) {
      allInteractions.push(...owner.contacts.map(contact => ({
        date: contact.createdAt,
        isContactAttempt: contact.type === ContactInteractionType.PHONE,
      })));
    }

    if (owner.qualityReferenceInteractions && owner.qualityReferenceInteractions.length > 0) {
      allInteractions.push(...owner.qualityReferenceInteractions.map(interaction => ({
        date: interaction.interactionAt,
        isContactAttempt: false,
      })));
    }

    if (owner.salesManagerInteractions && owner.salesManagerInteractions.length > 0) {
      allInteractions.push(...owner.salesManagerInteractions.map(interaction => ({
        date: interaction.interactionAt,
        isContactAttempt: interaction.interactionType === ContactInteractionType.PHONE,
      })));
    }

    if (owner.rentManagerInteractions && owner.rentManagerInteractions.length > 0) {
      allInteractions.push(...owner.rentManagerInteractions.map(interaction => ({
        date: interaction.interactionAt,
        isContactAttempt: interaction.interactionType === ContactInteractionType.PHONE,
      })));
    }

    allInteractions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    let count = 0;
    for (const interaction of allInteractions) {
      if (interaction.isContactAttempt) {
        count++;
      } else {
        break;
      }
    }
    return count;
  };

  useEffect(() => {
    if (isOpen) {
      setSessionAttempts(0);
      const existing = getConsecutivePhoneContacts();
      setShowClasserInjoignable(existing >= CONSECUTIVE_ATTEMPTS_THRESHOLD);
    }
  }, [isOpen, owner]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInteractionTypeChange = (type: ContactInteractionType) => {
    setFormData(prev => ({ ...prev, interactionType: type }));
  };

  const handleDateChange = (date: Date) => {
    const currentDate = formData.meetingAt;
    const newDate = updateTimeInDate(date, currentDate.getHours(), currentDate.getMinutes());
    setFormData((prev) => ({ ...prev, meetingAt: newDate }));
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes] = e.target.value.split(':').map(Number);
    const newDate = updateTimeInDate(formData.meetingAt, hours, minutes);
    setFormData((prev) => ({ ...prev, meetingAt: newDate }));
  };

  // Define handleFormClose first, before it's used
  const handleFormClose = () => {
    onClose();
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleUnreachable = async() => {
    await handleContactClick(owner.id, ContactInteractionType.PHONE, jwt);
    const newSessionAttempts = sessionAttempts + 1;
    setSessionAttempts(newSessionAttempts);
    const total = getConsecutivePhoneContacts() + newSessionAttempts;
    if (total >= CONSECUTIVE_ATTEMPTS_THRESHOLD) {
      setShowClasserInjoignable(true);
    } else {
      handleFormClose();
    }
  };

  const handlePermanentlyUnreachable = async() => {
    try {
      const response = await fetch(`/api/owners/${owner.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step: OWNER_STEPS.UNABLE_TO_REACH_SALES_MANAGER }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update owner step to permanently unreachable');
      }
      
      // Register the contact attempt
      await handleContactClick(owner.id, ContactInteractionType.PHONE, jwt);
      
      handleFormClose();
    } catch (error) {
      console.error('Error updating owner step:', error);
    }
  };

  const handleRefused = async() => {
    try {
      const response = await fetch(`/api/owners/${owner.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step: OWNER_STEPS.REFUSED }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update owner step to refused');
      }
      
      handleFormClose();
    } catch (error) {
      console.error('Error updating owner step:', error);
    }
  };

  const handleReachable = () => {
    setFormStep('response');
  };

  const handleScheduleMeeting = () => {
    const now = new Date();
    
    // Reset form data to ensure clean state when scheduling a meeting
    setFormData({
      interactionType: ContactInteractionType.PHONE,
      interactionAt: now,
      meetingAt: now,
      meetingWith: null,
      meetingWithType: 'sales-manager',
      comment: null,
    });
    setFormStep('full');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Format the dates to DB format ONLY when submitting to the API
    
    const userId = currentUser?.id ?? null;

    const finalData: InteractionFormData = {
      interactionType: formData.interactionType,
      meetingAt: formData.meetingAt.toISOString(),
      meetingWith: userId,
      meetingWithType: formData.meetingWithType,
      comment: formData.comment || '',
    };
    
    fetch(`/api/owners/${owner.id}/sales-manager-interactions`, {
      method: 'POST',
      headers: {
        'Authorization': JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        interactionType: formData.interactionType,
        interactionAt: formatDateTimeForDb(formData.interactionAt.toISOString()),
        meetingAt: formatDateTimeForDb(formData.meetingAt.toISOString()),
        meetingWith: userId,
        meetingWithType: formData.meetingWithType,
        comment: formData.comment || '',
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to save interaction');
        }
        onSubmit(finalData);
        handleFormClose();
      })
      .catch(error => {
        console.error('Error saving interaction:', error);
        // Still close the form for now, but in production you'd want to show an error message
        onSubmit(finalData);
        handleFormClose();
      });
  };

  const getCivilityLabel = (civility: number): string => {
    switch (civility) {
    case 1: return 'M.';
    case 2: return 'Mme';
    case 3: return 'Société';
    default: return '';
    }
  };

  const getFullName = (): string => {
    const civility = getCivilityLabel(owner.civility);
    const firstname = owner.firstname || '';
    const lastname = owner.lastname || '';
    return `${civility} ${firstname} ${lastname}`.trim();
  };

  const getFullAddress = (): string => {
    const address = owner.address || '';
    const postalCode = owner.postalCode || '';
    const city = owner.city || '';
    return `${address}${address ? ', ' : ''}${postalCode} ${city}`.trim();
  };

  const OwnerDetails: React.FC = () => (
    <>
      <div className="mb-2">
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-col space-y-2">
            <p className="font-medium">{getFullName()}</p>
            <div className="flex items-center space-x-2">
              {owner.isPotentialOwner !== undefined && owner.isPotentialOwner !== null ? (
                owner.isPotentialOwner ? (
                  <Badge mode="warning" text="Propriétaire potentiel" size="small"/>
                ) : (
                  <Badge mode="success" text="Propriétaire certain" size="small"/>
                )
              ) : (
                <Badge mode="warning" text="Statut non défini" size="small"/>
              )}
              <span className="text-sm text-gray-500">
                {owner.livingIn !== undefined && owner.livingIn !== null ? (owner.livingIn ? 'Résident' : 'Non résident') : 'Statut inconnu'}
              </span>
            </div>
            <p className="text-sm text-gray-500">{getFullAddress()}</p>
            <p className="text-sm text-gray-500">
              {owner.phone ? `Tél : ${owner.phone}` : 'Téléphone non renseigné'} 
              {owner.email ? ` | Email : ${owner.email}` : ' | Email non renseigné'}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <Label value="Détails du lot" />
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
          {(owner.lotNumber || owner.floorNumber || owner.doorNumber || owner.surface !== undefined || owner.dpe?.copro || owner.incomingCallCopro) ? (
            <div className="grid grid-cols-2 gap-4">
              {owner.lotNumber && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Lot :</span> {owner.lotNumber}
                </p>
              )}
              {owner.surface !== undefined && owner.surface !== null && owner.surface !== 0 && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Surface :</span> {owner.surface} m²
                </p>
              )}
              {owner.floorNumber !== undefined && owner.floorNumber !== null && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Étage :</span> {owner.floorNumber}
                </p>
              )}
              {owner.doorNumber !== undefined && owner.doorNumber !== null && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Porte :</span> {owner.doorNumber}
                </p>
              )}
              {(owner.dpe?.copro?.name || owner.incomingCallCopro?.name) && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Copropriété :</span> {owner.dpe?.copro?.name || owner.incomingCallCopro?.name}
                </p>
              )}
              {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) || (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) ? (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Adresse copro :</span> {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) ? String(owner.dpe.copro.addresses[0].address ?? '') : String(owner.incomingCallCopro?.addresses?.[0]?.address ?? '')}
                </p>
              ) : null}
              {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) || (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) ? (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Ville copro :</span> {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) ? `${owner.dpe.copro.addresses[0].postalCode} ${owner.dpe.copro.addresses[0].city}` : `${owner.incomingCallCopro?.addresses?.[0]?.postalCode || ''} ${owner.incomingCallCopro?.addresses?.[0]?.city || ''}`}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Aucune information complétée</p>
          )}
        </div>
      </div>

      {owner.dpe?.details && (
        <div className="mb-2">
          <Label value="DPE" />
          <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
            {owner.dpe?.details ? (
              <div className="grid grid-cols-2 gap-4">
                {owner.dpe.details.classeBilanDpe && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Classe énergie :</span> {String(owner.dpe.details.classeBilanDpe)}
                  </p>
                )}
                {owner.dpe.details.classeBilanGes && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Classe GES :</span> {String(owner.dpe.details.classeBilanGes)}
                  </p>
                )}
                {owner.dpe.details.epConso5UsagesM2 && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Consommation :</span> {Math.round(Number(owner.dpe.details.epConso5UsagesM2))} kWh/m²/an
                  </p>
                )}
                {owner.dpe.details.dateReceptionDpe && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Date de réception du DPE :</span> {new Date(owner.dpe.details.dateReceptionDpe.split(' ')[0]).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Aucune information complétée</p>
            )}
          </div>
        </div>
      )}
    </>
  );

  const renderInitialSelection = () => {
    return (
      <Popup 
        isOpen={isOpen} 
        onClose={handleFormClose} 
        title={`Contact avec ${getFullName()}`}
        width="800px"
      >
        <div className="space-y-6">
          <OwnerDetails />
          <div className='mb-2'>
            <Label value="Suivi du propriétaire" />
            <div className="mt-1 flex justify-center space-x-4 lg:space-x-10">
              <div className="flex flex-col">
                <Button color="red" onClick={handleUnreachable}>
                  <PhoneXMarkIcon className="mr-2 h-5 w-5 shrink-0" />
                  Injoignable
                </Button>
                {showClasserInjoignable && (
                  <button
                    onClick={handlePermanentlyUnreachable}
                    className="mt-2 cursor-pointer text-sm text-red-700 underline hover:text-red-800 focus:outline-none"
                    title={`${CONSECUTIVE_ATTEMPTS_THRESHOLD} tentatives consécutives sans succès`}
                  >
                    Classer définitivement injoignable
                  </button>
                )}
              </div>
              <div className="flex flex-col">
                <Button color="green" onClick={handleReachable}>
                  <CheckCircleIcon className="mr-2 h-5 w-5 shrink-0" />
                  A répondu
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  };

  const renderResponseSelection = () => (
    <Popup 
      isOpen={isOpen} 
      onClose={handleFormClose} 
      title={`Réponse de ${getFullName()}`}
      width="800px"
    >
      <div className="space-y-6">
        <OwnerDetails />
        <div className="mb-2">
          <Label value="Suivi du propriétaire" />
          <div className="mt-1 flex justify-center space-x-4 lg:space-x-10">
            <Button color="red" onClick={handleRefused}>
              <ArchiveBoxXMarkIcon className="mr-2 h-5 w-5 shrink-0" />
              Ne souhaite plus être recontacté
            </Button>
            <Button color="green" onClick={handleScheduleMeeting}>
              <CalendarDaysIcon className="mr-2 h-5 w-5 shrink-0" />
              Souhaite prendre rendez-vous
            </Button>
          </div>
        </div>
      </div>
    </Popup>
  );

  const renderFullForm = () => (
    <Popup 
      isOpen={isOpen} 
      onClose={handleFormClose} 
      title={`Enregistrer une prise de rdv avec ${getFullName()}`}
      width="800px"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <OwnerDetails />

          <div className="mb-2">
            <Label value="Lieu du rendez-vous" />
            <div className="mt-1 flex w-full justify-center">
              <RadioGroup value={formData.interactionType} onChange={handleInteractionTypeChange}>
                <RadioGroup.Label className="sr-only">Lieu du rendez-vous</RadioGroup.Label>
                <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                  <RadioGroup.Option value={ContactInteractionType.PHONE} className={({ checked }) => `${checked ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none`}>
                    <PhoneIcon className="h-5 w-5" />
                    Téléphone
                  </RadioGroup.Option>
                  <RadioGroup.Option value={ContactInteractionType.VISIO} className={({ checked }) => `${checked ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} ml-1 flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none`}>
                    <VideoCameraIcon className="h-5 w-5" />
                    Visio
                  </RadioGroup.Option>
                  <RadioGroup.Option value={ContactInteractionType.PHYSICAL} className={({ checked }) => `${checked ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} ml-1 flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none`}>
                    <UserGroupIcon className="h-5 w-5" />
                    Présentiel
                  </RadioGroup.Option>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="mb-2">
            <div className="mb-2 space-y-4">
              <div>
                <Label htmlFor="meetingAt" value="Date du rendez-vous" />
                <div className="mt-1">
                  <Datepicker
                    id="meetingAt"
                    name="meetingAt"
                    onChange={handleDateChange}
                    minDate={new Date()}
                    language="fr"
                    labelTodayButton="Aujourd'hui"
                    labelClearButton="Effacer"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="meetingTime" value="Heure du rendez-vous" />
                <div className="mt-1">
                  <input
                    type="time"
                    id="meetingTime"
                    name="meetingTime"
                    onChange={handleTimeChange}
                    value={formData.meetingAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <Label htmlFor="comment" value="Commentaire" />
            <Textarea
              id="comment"
              name="comment"
              value={formData.comment || ''}
              onChange={handleInputChange}
              rows={6}
              className="mt-1"
              placeholder="Ajouter des notes ou commentaires sur cette interaction..."
            />
          </div>
        </div>
      </form>
      <div className="mt-6 flex w-full justify-between">
        <Button color="gray" onClick={handleFormClose}>
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="blue"
        >
          Enregistrer le rendez-vous
        </Button>
      </div>
    </Popup>
  );

  if (formStep === 'initial') {
    return renderInitialSelection();
  }

  if (formStep === 'response') {
    return renderResponseSelection();
  }

  return renderFullForm();
};

export default InteractionForm; 